/**
 * Which web build this page is running, and whether the server has moved on
 * (7 October).
 *
 * A single-page app never asks for its page again. A tab opened before a
 * deploy goes on running the old code against the new API, and fails in ways
 * that make no sense on the screen in front of the person: Arthur's open tab
 * offered "Surrendered" with no confirmation step, the new API refused every
 * attempt with "Confirm it first", and there was nothing on his page to confirm
 * with. Six attempts, and nothing told him the page was out of date.
 *
 * So the API names the build it serves (`x-procura-web`, the entry bundle's file
 * name, read from index.html at startup), and every response is compared with
 * the build this page came from. When they differ the shell says so and asks for
 * a reload. Never an automatic reload: somebody may be half way through a form.
 *
 * In development the page is served by Vite from source, the API serves no
 * build, and the header is absent: nothing is compared.
 */

/**
 * This page's entry bundle, as the server names it: `index-XXXXXXXX.js`, read
 * from the page's own module script tag, which is what index.html loaded. Not
 * from `import.meta.url`: the build splits the code into more than one chunk,
 * and which chunk this module lands in is the bundler's choice, not ours.
 * Null in development, where the tag is `/src/main.tsx`.
 */
export const WEB_BUILD: string | null = (() => {
  const src = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? '';
  const name = src.split('/').pop() ?? '';
  return /^index-[\w-]+\.js$/.test(name) ? name : null;
})();

type Listener = () => void;
const listeners = new Set<Listener>();
let outOfDate = false;

/** Called with every API response's header. Announces once, the first time they differ. */
export function noteServedBuild(served: string | null): void {
  if (outOfDate || !served || !WEB_BUILD || served === WEB_BUILD) return;
  outOfDate = true;
  for (const l of listeners) l();
}

export function isOutOfDate(): boolean {
  return outOfDate;
}

export function onOutOfDate(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
